import React, { useState, useEffect } from 'react';
import { Send, User, ShieldCheck, Clock } from 'lucide-react';

interface Message {
    ID: number;
    Sender: 'student' | 'admin';
    Content: string;
    Timestamp: string;
}

interface DashboardMessagingProps {
    context: string; // 'admissions', 'accommodation', etc.
}

const DashboardMessaging: React.FC<DashboardMessagingProps> = ({ context }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/student/messages?context=${context}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 5000); // Polling
        return () => clearInterval(interval);
    }, [context]);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/student/messages', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Context: context,
                    Content: newMessage
                })
            });

            if (res.ok) {
                setNewMessage('');
                fetchMessages();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-[380px] sm:h-[450px] max-h-[70vh] bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#009B8F]" />
                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Expert Support Chat</span>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
                {messages.length === 0 && (
                    <div className="text-center py-10">
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">No messages yet</p>
                    </div>
                )}
                {messages.map((msg) => (
                    <div key={msg.ID} className={`flex flex-col ${msg.Sender === 'student' ? 'items-end' : 'items-start'}`}>
                        <div className={`
                            max-w-[85%] p-3.5 rounded-2xl text-[13px] leading-relaxed
                            ${msg.Sender === 'student'
                                ? 'bg-[#009B8F] text-white rounded-tr-none shadow-sm'
                                : 'bg-gray-100 text-slate-700 border border-gray-200 rounded-tl-none'}
                        `}>
                            {msg.Content}
                        </div>
                        <span className="text-[9px] text-slate-400 mt-1.5 flex items-center gap-1 px-1 font-bold uppercase tracking-tighter">
                            <Clock size={8} /> {new Date(msg.Timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ))}
            </div>

            <form onSubmit={handleSend} className="p-3 bg-gray-50 border-t border-gray-200 flex gap-2 items-center">
                <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 bg-white border border-gray-200 outline-none px-4 py-2.5 rounded-xl text-xs text-slate-700 placeholder:text-slate-400 focus:border-[#009B8F] transition-all"
                />
                <button
                    disabled={loading || !newMessage.trim()}
                    type="submit"
                    className="p-2.5 bg-[#009B8F] text-white rounded-xl hover:bg-[#007b72] active:scale-95 transition-all disabled:opacity-30 flex-shrink-0 shadow-sm"
                >
                    <Send size={16} />
                </button>
            </form>
        </div>
    );
};

export default DashboardMessaging;
